// frontend/src/api/dashboard-extended.ts
import { api } from "./client.js";
import type { AttributionModels } from "./sales.js";
export type { AttributionModels } from "./sales.js";

export interface PinsSummary {
  total_sales: number; net_revenue: number; ad_spend: number;
  roas: number | null; net_roas: number | null; buyout_rate: number | null;
  orders: number; buyouts: number; cancelled: number; in_transit: number;
}
export interface PinsWithComparison {
  current: PinsSummary;
  previous: PinsSummary;
  period_days: number;
}
export interface WebAnalytics {
  conversion_rate: number | null; users: number; sessions: number;
  pages_per_session: number | null; avg_session_duration: number | null;
  bounce_rate: number | null; new_users: number; new_users_pct: number | null;
  sessions_with_cart: number;
}
export interface StoreMetrics {
  order_revenue: number; orders: number; returns: number; taxes: number;
  aov: number | null; true_aov: number | null;
  new_customers: number; returning_customers: number;
  gross_sales: number; units_sold: number;
}
export interface AttributionRow {
  source: string; is_sr: boolean; budget: number; spend: number; cv: number;
  roas: number | null; clicks: number; impressions: number;
  ctr: number | null; cpm: number | null; cpa: number | null;
  nc_cv: number; nc_roas: number | null; nc_cpa: number | null;
}
export interface SourceSlice {
  visits: number; buyout_orders: number; buyout_revenue: number;
}
export interface SalesBySource {
  paid: SourceSlice;
  organic_other: SourceSlice;
  total: SourceSlice;
  paid_buyout_share: number | null;
}
export interface DashboardExtended {
  period_days: number;
  lookback_days: number;
  pins: PinsWithComparison;
  web: WebAnalytics;
  store: StoreMetrics;
  attribution: AttributionRow[];
  bySource: SalesBySource;
  attributionModels: AttributionModels;
  trafficSources: TrafficSources;
  adPurchaseLag: AdPurchaseLag;
  buyerJourney: BuyerJourney;
  adReconciliation: AdReconciliation;
  botStats: BotStats;
  customerMix: CustomerMix;
}

export interface BotStats { filtered_clients: number; filtered_visits: number; exempt_buyers: number; peak_threshold: number }
export interface CustomerMix {
  new_customers: number;
  returning_customers: number;
  new_buyout_revenue: number;
  returning_buyout_revenue: number;
  new_buyout_orders: number;
  untracked_buyout_revenue: number;
}

export interface AdReconDay { day: string; impressions: number; clicks: number; ad_visits: number; spend: number | null }
export interface AdReconciliation {
  days: AdReconDay[];
  total_impressions: number; total_clicks: number; total_ad_visits: number;
}

export interface JourneyGroup { key: string; label: string; orders: number; customers: number; revenue: number }
export interface JourneyDay { day: string; ad_direct: number; ad_assisted: number; organic: number; untracked: number }
export interface JourneyTimeline { visits: JourneyDay[]; carts: JourneyDay[]; orders: JourneyDay[]; buyouts: JourneyDay[] }
export interface BuyerJourney { groups: JourneyGroup[]; days: JourneyDay[]; timeline: JourneyTimeline; total_orders: number; total_revenue: number }

export interface LagBucket { key: string; label: string; orders: number; revenue: number }
export interface AdPurchaseLag { buckets: LagBucket[]; total_buyout_orders: number }

export interface TrafficDay { day: string; fresh_ad: number; lagged_ad: number; organic: number; spend: number | null }
export interface TrafficSources {
  days: TrafficDay[];
  total: { fresh_ad: number; lagged_ad: number; organic: number };
  max_spend_day: string | null;
}

export const fetchDashboardExtended = (days: number, lookback = 30) =>
  api<DashboardExtended>(`/api/dashboard/extended?days=${days}&lookback=${lookback}`);
